'use client'
import Image from 'next/image'
import Link from 'next/link'
import { useLanguage } from '@/lib/language-context'
import { Phone, Mail, MapPin, ArrowRight } from 'lucide-react'

export function Footer() {
  const { t } = useLanguage()
  const year = new Date().getFullYear()

  return (
    <footer style={{ background: '#150b03', borderTop: '3px solid #E67E22' }}>
      {/* Orange glow top */}
      <div
        style={{
          height: '1px',
          background: 'linear-gradient(90deg, transparent, rgba(243,156,18,0.5), transparent)',
          marginBottom: '0',
        }}
      />

      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
          {/* Brand Column */}
          <div className="md:col-span-1">
            <div className="flex items-center gap-3 mb-4">
              <div
                style={{
                  width: '44px',
                  height: '44px',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  background: 'white',
                  border: '2px solid rgba(243,156,18,0.4)',
                  flexShrink: 0,
                }}
              >
                <Image src="/logo.jpeg" alt="Pune Pratham" width={44} height={44} className="object-cover w-full h-full" />
              </div>
              <div>
                <div
                  style={{
                    fontFamily: 'Poppins, sans-serif',
                    fontWeight: 700,
                    fontSize: '1.1rem',
                    color: '#F39C12',
                  }}
                >
                  Pune Pratham
                </div>
                <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.75rem' }}>Mayor&apos;s Initiative</div>
              </div>
            </div>
            <p style={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.85rem', lineHeight: 1.7 }}>
              {t('footerDesc')}
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4
              style={{
                fontFamily: 'Poppins, sans-serif',
                fontWeight: 700,
                fontSize: '0.9rem',
                color: '#F39C12',
                marginBottom: '16px',
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
              }}
            >
              {t('quickLinks')}
            </h4>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {[
                { href: '/', label: t('navHome') },
                { href: '/raise-complaint', label: t('raiseComplaint') },
                { href: '/track-complaint', label: t('trackComplaint') },
                { href: '/about', label: t('navAbout') },
                { href: '/feedback', label: t('navFeedback') || 'Feedback' },
                { href: '/login', label: t('navAdminLogin') },
              ].map(link => (
                <li key={link.href}>
                  <Link
                    href={link.href}
                    style={{
                      color: 'rgba(255,255,255,0.6)',
                      textDecoration: 'none',
                      fontSize: '0.875rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      transition: 'color 0.2s ease',
                    }}
                    onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = '#F39C12' }}
                    onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = 'rgba(255,255,255,0.6)' }}
                  >
                    <ArrowRight className="w-3 h-3" style={{ color: '#E67E22', flexShrink: 0 }} />
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Support */}
          <div>
            <h4
              style={{
                fontFamily: 'Poppins, sans-serif',
                fontWeight: 700,
                fontSize: '0.9rem',
                color: '#F39C12',
                marginBottom: '16px',
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
              }}
            >
              {t('support')}
            </h4>
            <p style={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.85rem', lineHeight: 1.7 }}>
              {t('supportDesc')}
            </p>
          </div>

          {/* Contact */}
          <div>
            <h4
              style={{
                fontFamily: 'Poppins, sans-serif',
                fontWeight: 700,
                fontSize: '0.9rem',
                color: '#F39C12',
                marginBottom: '16px',
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
              }}
            >
              Contact Us
            </h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <a
                href="tel:02025501000"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  color: 'rgba(255,255,255,0.6)',
                  textDecoration: 'none',
                  fontSize: '0.875rem',
                  transition: 'color 0.2s',
                }}
                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = '#F39C12' }}
                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = 'rgba(255,255,255,0.6)' }}
              >
                <div style={{
                  width: '32px', height: '32px', borderRadius: '8px',
                  background: 'rgba(243,156,18,0.1)', border: '1px solid rgba(243,156,18,0.3)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                }}>
                  <Phone className="w-4 h-4" style={{ color: '#F39C12' }} />
                </div>
                020-25501000
              </a>
              <a
                href="mailto:mayor@punecorporation.org"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  color: 'rgba(255,255,255,0.6)',
                  textDecoration: 'none',
                  fontSize: '0.875rem',
                  transition: 'color 0.2s',
                }}
                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = '#F39C12' }}
                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = 'rgba(255,255,255,0.6)' }}
              >
                <div style={{
                  width: '32px', height: '32px', borderRadius: '8px',
                  background: 'rgba(243,156,18,0.1)', border: '1px solid rgba(243,156,18,0.3)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                }}>
                  <Mail className="w-4 h-4" style={{ color: '#F39C12' }} />
                </div>
                mayor@punecorporation.org
              </a>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '10px',
                  color: 'rgba(255,255,255,0.6)',
                  fontSize: '0.875rem',
                }}
              >
                <div style={{
                  width: '32px', height: '32px', borderRadius: '8px',
                  background: 'rgba(243,156,18,0.1)', border: '1px solid rgba(243,156,18,0.3)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  marginTop: '2px',
                }}>
                  <MapPin className="w-4 h-4" style={{ color: '#F39C12' }} />
                </div>
                Pune Municipal Corporation, Shivajinagar, Pune - 411005
              </div>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div
          style={{
            borderTop: '1px solid rgba(243,156,18,0.15)',
            paddingTop: '24px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '12px',
          }}
        >
          <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.8rem' }}>
            {t('footerCopy').replace('{year}', String(year))}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.8rem' }}>Website Visitors:</span>
            <img
              src="https://api.visitorbadge.io/api/visitors?path=https%3A%2F%2Fpunepratham.in&countColor=%23E67E22&style=flat"
              alt="Visitor Count"
              className="h-5"
            />
          </div>
          <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.8rem' }}>
            Developed and maintained by{' '}
            <Link
              href="https://dhyastech.com/"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#F39C12', textDecoration: 'none', transition: 'color 0.2s' }}
              onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = '#FF9800' }}
              onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = '#F39C12' }}
            >
              Dhyas Technologies
            </Link>
          </div>
        </div>
      </div>
    </footer>
  )
}
